import {CommonModule} from '@angular/common';
import {Component, HostBinding, HostListener, Input} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {PostApiModel} from '../../api/models/post-api.model';
import {ShareCardApiModel} from '../../api/models/share-card.api-model';
import {CurrentUserViewModel} from '../../core/models/current-user.view-model';
import {MediaTypeEnum} from '../../core/enums/media-type.enum';
import {PipesModule} from '../../core/pipes/pipes.module';
import {ThumbnailModule} from '../thumbnail/thumbnail.module';
import {PostViewModel} from '../timeline/post.view-model';
import {PostMediaGridComponent} from '../timeline/post-media-grid/post-media-grid.component';
import {PostSourceHeaderComponent} from '../timeline/post-source-header/post-source-header.component';

@Component({
  selector: 'app-share-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    PipesModule,
    ThumbnailModule,
    PostSourceHeaderComponent,
    PostMediaGridComponent,
  ],
  templateUrl: './share-card.component.html',
  styleUrl: './share-card.component.scss'
})
export class ShareCardComponent {
  private shareCard: ShareCardApiModel | null = null;
  public sourcePost: PostViewModel | null = null;

  @Input() public set card(card: ShareCardApiModel | null) {
    this.shareCard = card;
    this.sourcePost = card?.available && card.type === 'post' && card.post
      ? new PostViewModel(card.post as PostApiModel).getShareSourcePost()
      : null;
  }

  public get card(): ShareCardApiModel | null {
    return this.shareCard;
  }

  @Input() public compact = false;
  @Input() public mode: 'live' | 'preview' = 'live';
  @Input() public currentUser: CurrentUserViewModel | null = null;

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
  ) {
  }

  @HostBinding('attr.role')
  public get hostRole(): string | null {
    return this.isInteractive ? 'link' : null;
  }

  @HostBinding('attr.tabindex')
  public get hostTabIndex(): number | null {
    return this.isInteractive ? 0 : null;
  }

  public get displayTitle(): string {
    if (!this.card?.available) {
      return this.translate.instant(this.card?.title || 'shared.share.unavailable.title');
    }

    const post = this.sourcePost;
    if (!post) {
      return '';
    }

    if (post.project?.displayVehicleIdentity) {
      return post.project.displayVehicleIdentity;
    }

    if (post.event?.name) {
      return post.event.name;
    }

    return post.postedBy?.username || '';
  }

  public get displaySubtitle(): string {
    if (!this.card?.available) {
      return this.translate.instant(this.card?.content || 'shared.share.unavailable.content');
    }

    const post = this.sourcePost;
    if (!post) {
      return '';
    }

    if (post.project?.alias) {
      return post.project.alias;
    }

    return post.postedBy?.username || '';
  }

  public get primaryImage(): any | null {
    const media = this.sourcePost?.media || [];
    const candidate = media[0] as any;
    if (!candidate || candidate?.type === MediaTypeEnum.VIDEO) {
      return null;
    }

    return candidate;
  }

  public get postRoute(): any[] | null {
    if (!this.sourcePost?.id) {
      return null;
    }

    return ['/posts', this.sourcePost.id];
  }

  public get isPreviewMode(): boolean {
    return this.mode === 'preview';
  }

  public get isInteractive(): boolean {
    return !this.isPreviewMode && !!this.postRoute;
  }

  @HostListener('click', ['$event'])
  public onCardClick(event?: Event): void {
    const target = event?.target as Element | null;
    if (!this.isInteractive || !this.postRoute || event?.defaultPrevented
      || target?.closest?.('app-post-media-grid, a, button, input, video, [role="button"], [contenteditable="true"]')) {
      return;
    }

    void this.router.navigate(this.postRoute);
  }

  @HostListener('keydown.enter', ['$event'])
  public onEnter(event: KeyboardEvent): void {
    if (!this.isInteractive || event.defaultPrevented || event.target !== event.currentTarget) {
      return;
    }

    event.preventDefault();
    this.onCardClick();
  }

  @HostListener('keydown.space', ['$event'])
  public onSpace(event: KeyboardEvent): void {
    if (!this.isInteractive || event.defaultPrevented || event.target !== event.currentTarget) {
      return;
    }

    event.preventDefault();
    this.onCardClick();
  }
}
